import { onMonthCellClick, onMonthCellDoubleClick } from '../actions/monthCellOnClickActions';
import { openDeleteModal } from '../actions/openDeleteModal';
import { defaultCommandTimeout, executeCommand } from 'owa-calendar-command-executor';
import { focusCalendarAgenda } from '../actions/focusCalendarAgenda';
import { logUsage } from 'owa-analytics';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyOpenCalendarFullCompose } from 'owa-calendar-compose-form-lifecycle';
import {
    shouldOpenInCompose,
    canForward,
    isForwardDisabled,
    canReply,
} from 'owa-calendar-event-capabilities';
import { isCalendarEventLocalLie } from 'owa-calendar-event-local-lie';
import { openItemPeek } from 'owa-calendar-peek';
import { lazyOpenCalendarReadForm } from 'owa-calendar-read-form-lifecycle';
import { surfaceStore } from 'owa-calendar-surface-surface-store';
import { openItemContextMenu } from 'owa-calendar-surface-view-store';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import EventScope from 'owa-service/lib/contract/EventScope';
import {
    CalendarSurfaceStore,
    SurfaceEventHandlers,
    updateAllDayWellExpanded,
    updateHoveredItem,
    getTargetDivCSSSelector,
    lockSelectedItem,
} from 'owa-calendar-surface-dategrid';
import {
    CalendarMonthDayCellModel,
    updateSelectedDate,
    updateSelectedItem,
    createOverflowIndicatorEventHandlers,
} from 'owa-calendar-surface';
import { toggleAgendaPane, getIsAgendaPaneShown } from 'owa-calendar-layout';
import { forwardEvent, replyToMeeting } from 'owa-calendar-surface-events-actions';
import {
    KeyboardCalendarEventInteraction,
    getKeyboardCalendarEventInteraction,
} from 'owa-calendar-event-interaction-schema';

const ActionSource = 'Surface';

/**
 * Event handlers for items both on Day/Week and Month view
 */
export let owaCalendarItemsEventHandlers: SurfaceEventHandlers = {
    onMouseUp: evt => evt.stopPropagation(),
    onMouseDown: onItemMouseDown,
    onMouseOver: onItemMouseOver,
    onMouseLeave: onItemMouseOut,
    onClick: onItemClick,
    onDoubleClick: onItemDoubleClick,
    onContextMenu: onItemContextMenu,
    onKeyDown: onItemKeyDown,
};

/**
 * Event handlers for overflow items in month view
 */
export let monthOverflowItemsHandlers: SurfaceEventHandlers = createOverflowIndicatorEventHandlers(
    monthOverflowIndicatorOnClick
);

/**
 * Event handlers for overflow items in week view
 */
export let weekOverflowItemsHandlers: SurfaceEventHandlers = createOverflowIndicatorEventHandlers(
    weekOverflowIndicatorOnClick
);

/**
 * Event handlers for month day cells
 */
export let monthDayCellsEventHandlers: SurfaceEventHandlers = {
    onClick: onMonthDayCellClick,
    onDoubleClick: onMonthDayCellDoubleClick,
};

function onItemMouseDown(evt: React.MouseEvent<HTMLDivElement>) {
    // This is needed to prevent the column from getting the mouse down events.
    if (!surfaceStore.enableDragDrop) {
        evt.stopPropagation();
    }
}

function onItemMouseOver(this: CalendarEvent) {
    updateHoveredItem(this, surfaceStore);
}

function onItemMouseOut(this: CalendarEvent) {
    updateHoveredItem(null, surfaceStore);
}

function onItemClick(this: CalendarEvent, evt: React.MouseEvent<HTMLDivElement>) {
    logUsage('onSurfaceEventClick', { currentDateRangeType: surfaceStore.dateRangeType });
    const currentTarget = evt.currentTarget;
    selectItem(surfaceStore, this, currentTarget);

    executeCommand(() => {
        if (shouldOpenInCompose(this)) {
            openFullComposeInEditMode(this);
        } else {
            // lock the selectedItem while the peek is being opened, to avoid it getting cleared in meantime
            lockSelectedItem(surfaceStore);
            openItemPeek.importAndExecute(this);
        }
    }, defaultCommandTimeout);

    evt.stopPropagation();
}

function onItemDoubleClick(this: CalendarEvent, evt: React.MouseEvent<HTMLDivElement>) {
    logUsage('onSurfaceEventDoubleClick', { currentDateRangeType: surfaceStore.dateRangeType });
    evt.preventDefault();
    evt.stopPropagation();
    const currentTarget = evt.currentTarget;
    executeCommand(() => {
        selectAndOpenFullItem(surfaceStore, this, currentTarget);
    }, defaultCommandTimeout);
}

function onItemContextMenu(this: CalendarEvent, evt: React.MouseEvent<HTMLDivElement>) {
    // when showing a context menu, we also select the item but don't show the context menu
    evt.persist();
    evt.stopPropagation();
    evt.preventDefault();
    selectItem(surfaceStore, this, evt.currentTarget);
    openItemContextMenu(this, evt);
}

function onItemKeyDown(this: CalendarEvent, evt: React.KeyboardEvent<HTMLDivElement>) {
    switch (getKeyboardCalendarEventInteraction(evt)) {
        case KeyboardCalendarEventInteraction.Open:
            // default is prevented to not accidentally select items once the new form opens
            evt.preventDefault();
            selectAndOpenFullItem(surfaceStore, this, evt.currentTarget);
            break;
        case KeyboardCalendarEventInteraction.Delete:
            // default is prevented to not accidentally hit the delete key after refocusing
            // in the event that a new form opens that would respond to it from onItemDelete
            evt.preventDefault();
            selectItem(surfaceStore, this, evt.currentTarget);
            openDeleteModal(this, ActionSource);
            break;
        case KeyboardCalendarEventInteraction.Forward:
            if (canForward(this) && !isForwardDisabled(this)) {
                evt.preventDefault();
                forwardEvent(this, EventScope.ThisInstanceOnly, ActionSource);
            }
            break;
        case KeyboardCalendarEventInteraction.ReplyAll:
            if (canReply(this)) {
                evt.preventDefault();
                replyToMeeting(this, true /* isReplyAll */, ActionSource);
            }
            break;
        case KeyboardCalendarEventInteraction.Reply:
            if (canReply(this)) {
                evt.preventDefault();
                replyToMeeting(this, false /* isReplyAll */, ActionSource);
            }
            break;
    }
}

/**
 * Called when an item has to be selected.
 * @surfaceStore The surface store
 * @selectedItem - the current selected item. This can be passed as null if one has a customTarget that is being sent
 * @target - target of the selected item
 * @customTarget - one can pass a custom selector. For instance, agenda peek uses this to select calendar items on it.
 */
export function selectItem(
    surfaceStore: CalendarSurfaceStore,
    selectedItem: CalendarEvent,
    target: HTMLDivElement,
    customTarget: string = null
) {
    let targetDiv = !customTarget ? getTargetDivCSSSelector(target) : customTarget;
    updateSelectedItem(selectedItem, targetDiv, surfaceStore, customTarget);
}

function selectAndOpenFullItem(
    surfaceStore: CalendarSurfaceStore,
    selectedItem: CalendarEvent,
    target: HTMLDivElement
) {
    selectItem(surfaceStore, selectedItem, target);
    if (shouldOpenInCompose(selectedItem, true /* isDoubleClick */)) {
        openFullComposeInEditMode(selectedItem);
    } else if (!isCalendarEventLocalLie(selectedItem)) {
        lazyOpenCalendarReadForm.importAndExecute({
            itemId: selectedItem.ItemId,
            entrySource: 'SurfaceItemClicked',
        });
    }
}

function openFullComposeInEditMode(item: CalendarEvent) {
    const eventScope = item.IsRecurring
        ? isFeatureEnabled('mon-cal-item-doubleclick')
            ? EventScope.ThisInstanceOnly
            : EventScope.AllInstancesInSeries
        : EventScope.Default;
    lazyOpenCalendarFullCompose.importAndExecute({
        fullComposeEntrySource: 'DraftSurface',
        eventScope: eventScope,
        eventUpdates: item,
    });
}

function onMonthDayCellClick(
    this: CalendarMonthDayCellModel,
    evt: React.MouseEvent<HTMLDivElement>
) {
    onMonthCellClick(this.date, evt.currentTarget);
}

function onMonthDayCellDoubleClick(
    this: CalendarMonthDayCellModel,
    evt: React.MouseEvent<HTMLDivElement>
) {
    onMonthCellDoubleClick(this.date, evt.currentTarget);
}

/**
 * Overflow Indicator OnClick
 */
function monthOverflowIndicatorOnClick(
    surfaceItem: CalendarEvent,
    evt: React.MouseEvent<HTMLDivElement>
) {
    evt.stopPropagation();
    selectItem(surfaceStore, surfaceItem, evt.currentTarget);
    updateSelectedDate(surfaceItem.Start, surfaceStore);
    if (!getIsAgendaPaneShown()) {
        toggleAgendaPane();
    }
    focusCalendarAgenda();
}

/**
 * Overflow Indicator OnClick
 */
function weekOverflowIndicatorOnClick(
    surfaceItem: CalendarEvent,
    evt: React.MouseEvent<HTMLDivElement>
) {
    evt.stopPropagation();
    updateAllDayWellExpanded(surfaceStore, true, 'ExpandButton');
    logUsage('clickOverflowAllDayWell', null /* customData */, { isCore: true });
}
